import * as React from 'react';

import { useTranslation } from '@crehana/i18n';
import ArrowLeft from '@crehana/icons/outline/ArrowIosLeft';
import ArrowRight from '@crehana/icons/outline/ArrowIosRight';
import { CustomNextImage as Image } from '@crehana/next';
import { useIsClient, useMediaQuery } from '@crehana/react-hooks';
// biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
import { type CuiTheme, IconButton } from '@crehana/ui';
import { useInterests } from '@crehana/web/components/InterestsPicker/hooks';

import {
  DraggableContentContainer,
  SliderContainer,
  Wrapper,
} from './CategorySlider.styles';
import {
  AVAILABLE_CATEGORY_IMAGES,
  DEFAULT_TRANSLATION_DESKTOP,
  DEFAULT_TRANSLATION_MOBILE,
} from './constants';
import { imagesData } from './images-data';
import Loader from './Loader';
import SliderItem from './SliderItem';
import type { SliderProps } from './types';
import { getTranslationInPx } from './utils';

const ProfessorImages: React.FC<
  React.PropsWithChildren<{ imageUrl: string; label?: string }>
  // TODO: talk with the code owner to fix this code so we can enable this rule again
  // eslint-disable-next-line react/display-name
> = React.memo(({ imageUrl, label }) => {
  const { t } = useTranslation();

  return (
    <div
      className="absolute m-auto top-0"
      css={`
        width: 260px;
        height: 420px;
        ${({ theme }: { theme: CuiTheme }) => theme.breakpoints.up('sm')} {
          width: 440px;
          height: 460px;
        }
      `}
    >
      <Image
        src={imagesData[imageUrl as unknown as keyof typeof imagesData]}
        alt={label}
        objectFit="contain"
        layout="fill"
      />
      <h4 className="font-h4 text-white text-center z-5 absolute inset-x-0 bottom-0">
        {label || t('MICRODEGREE_SLIDER_SUBTITLE')}{' '}
      </h4>
    </div>
  );
});

export const Slider: React.FC<
  React.PropsWithChildren<{ titleCategory?: string }>
> = ({ titleCategory }) => {
  const { categories, loading } = useInterests();
  const { matches: isSm } = useMediaQuery('sm');
  const { matches: isMd } = useMediaQuery('md');
  const { matches: isLg } = useMediaQuery('lg');
  const isXs = !isSm && !isMd && !isLg;

  const [selectedIndex, setSelectedIndex] = React.useState(isXs ? 1 : 2);
  const [imageUrl, setImageUrl] = React.useState<string>();
  const [translateX, setTranslateX] = React.useState(
    isXs ? DEFAULT_TRANSLATION_MOBILE : DEFAULT_TRANSLATION_DESKTOP,
  );

  const getImagePath = (slug?: string): string => {
    if (!slug) {
      return `${AVAILABLE_CATEGORY_IMAGES[0]}.png`;
    }

    const imageName = slug.replace('/', '');

    if (AVAILABLE_CATEGORY_IMAGES.includes(imageName)) {
      return `${imageName}.png`;
    }
    return `${AVAILABLE_CATEGORY_IMAGES[0]}.png`;
  };

  const dragContainer = (newIndex: number) => {
    const newTranslation = getTranslationInPx({
      toIndex: newIndex,
      isMobile: isXs,
    });

    setTranslateX(newTranslation);
    setSelectedIndex(newIndex);
    setImageUrl(getImagePath(categories[newIndex].slug));
  };

  const goNext = () => {
    if (selectedIndex < categories.length - 1) {
      const newIndex = selectedIndex + 1;

      dragContainer(newIndex);
    } else {
      dragContainer(0);
    }
  };

  const goPrev = () => {
    if (selectedIndex > 0) {
      const newIndex = selectedIndex - 1;

      dragContainer(newIndex);
    } else {
      dragContainer(categories.length - 1);
    }
  };

  const handleCategoryClick = ({
    index,
    url,
  }: {
    index: number;
    url: string;
  }) => {
    if (index === selectedIndex) {
      window.location.pathname = url;
    } else {
      dragContainer(index);
    }
  };

  const ArrowLeftButton = (
    <IconButton
      isWhite
      withoutBorders
      onClick={goPrev}
      icon={{
        component: ArrowLeft,
        size: 32,
      }}
    />
  );

  const ArrowRightButton = (
    <IconButton
      isWhite
      withoutBorders
      onClick={goNext}
      icon={{
        component: ArrowRight,
        size: 32,
      }}
    />
  );

  if (loading || !categories.length) {
    return <Loader />;
  }

  return (
    <Wrapper className="flex flex-col items-center justify-center relative">
      <ProfessorImages
        imageUrl={imageUrl || `${AVAILABLE_CATEGORY_IMAGES[0]}.png`}
        label={titleCategory}
      />

      <div className="absolute bottom-0 overflow-hidden w-full sm:w-full left-0 flex flex-col sm:flex-row items-center justify-center">
        <div className="hidden sm:block">{ArrowLeftButton}</div>
        <SliderContainer className="overflow-hidden py-48 m-auto">
          <DraggableContentContainer
            translateX={translateX}
            className="flex flex-row items-start"
          >
            {categories.map(
              ({ icon, name, originalId, absoluteUrl }, index) => (
                <SliderItem
                  handleClick={() =>
                    handleCategoryClick({ index, url: absoluteUrl || '' })
                  }
                  text={name}
                  isSelected={selectedIndex === index}
                  key={originalId}
                  icon={icon}
                />
              ),
            )}
          </DraggableContentContainer>
        </SliderContainer>
        <div className="flex">
          <div className="sm:hidden">{ArrowLeftButton}</div>
          {ArrowRightButton}
        </div>
      </div>
    </Wrapper>
  );
};

const CategorySlider: React.FC<React.PropsWithChildren<SliderProps>> = ({
  titleCategory,
}) => {
  const isClient = useIsClient();

  if (!isClient) {
    return <Loader />;
  }

  return <Slider titleCategory={titleCategory} />;
};

export default React.memo(CategorySlider);
